import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    CircularProgress,
    Chip,
    Stack,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    CloudDownload as DownloadIcon,
    Error as ErrorIcon,
    Description as FileIcon,
    Schedule as ScheduleIcon,
    VerifiedUser as AckIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { projectsService } from '../services/projectsService';
import { TransmittalDetails } from '../types';
import { FileCategoryChip } from '../components/FileCategoryChip';

/* ──────────────────────────────────────────────── *
 *  Nautilus Console Design System — Token Layer    *
 *  Derived from system.md                          *
 * ──────────────────────────────────────────────── */
const NAUTILUS = {
    deepOcean: '#0f2134',
    sand: '#ccba9b',
    abyssal: '#0a1622',
};

/** Resolve design tokens for the active colour scheme */
const useTokens = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return {
        isDark,
        // Surface hierarchy
        rack: isDark ? NAUTILUS.abyssal : '#f5f5f5',      // Background "base"
        panel: isDark ? NAUTILUS.deepOcean : '#ffffff',      // Card / widget
        inset: isDark ? alpha('#000', 0.18) : alpha('#000', 0.03), // Recessed areas
        // Text
        ink: isDark ? '#ffffff' : NAUTILUS.deepOcean,
        inkSecond: isDark ? '#b0bec5' : '#546e7a',
        inkMuted: isDark ? alpha('#fff', 0.38) : alpha('#000', 0.38),
        // Borders
        border: isDark ? '#2c3e50' : '#e0e0e0',
        borderSoft: isDark ? alpha(NAUTILUS.sand, 0.12) : alpha(NAUTILUS.deepOcean, 0.08),
        // Accent (Sand) — always the same
        accent: NAUTILUS.sand,
        accentHover: isDark ? alpha(NAUTILUS.sand, 0.85) : alpha(NAUTILUS.sand, 0.9),
        // Semantic
        successBg: isDark ? alpha('#4caf50', 0.12) : alpha('#4caf50', 0.08),
        successFg: isDark ? '#81c784' : '#2e7d32',
        infoBg: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.sand, 0.1),
        infoFg: isDark ? NAUTILUS.sand : '#8a6d3b',
    };
};

/* ──────── Helpers ──────── */

const formatBytes = (bytes?: number): string => {
    if (!bytes || bytes === 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatRevision = (rev?: string): string => {
    if (!rev) return '—';
    if (rev.includes('_')) {
        const parts = rev.split('_');
        if (parts.length === 2) {
            return `${parts[1]} ${parts[0]}`;
        }
    }
    return rev;
};

/* ─────────────────────────────────────────────────
 *  Component
 * ───────────────────────────────────────────────── */

export const TransmittalDownloadPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const t = useTokens();

    const [transmittal, setTransmittal] = useState<TransmittalDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<number[]>([]);
    const [downloading, setDownloading] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);

    /* ── Fetch ── */
    useEffect(() => {
        if (!token) { setError('Invalid access token'); setLoading(false); return; }

        (async () => {
            try {
                const data = await projectsService.getTransmittal(token);
                setTransmittal(data);
                setSelectedFiles(data.files.map(f => f.id));
                if (data.status === 'acknowledged') setAcknowledged(true);
            } catch (err: any) {
                if (err.response?.status === 410) setError('This transmittal link has expired.');
                else if (err.response?.status === 404) setError('Transmittal not found.');
                else setError('Failed to load transmittal details.');
            } finally { setLoading(false); }
        })();
    }, [token]);

    /* ── Actions ── */
    const handleDownload = async () => {
        if (!token || selectedFiles.length === 0) return;
        setDownloading(true);
        try {
            const result = await projectsService.downloadTransmittalFiles(token, selectedFiles);
            if (result?.autoAcknowledged) {
                setAcknowledged(true);
                if (transmittal) setTransmittal({ ...transmittal, status: 'acknowledged' });
            }
        }
        catch (err) { console.error(err); }
        finally { setDownloading(false); }
    };

    const handleSelectAll = (checked: boolean) => {
        if (transmittal) setSelectedFiles(checked ? transmittal.files.map(f => f.id) : []);
    };

    const handleSelectFile = (id: number, checked: boolean) => {
        setSelectedFiles(prev => checked ? [...prev, id] : prev.filter(fid => fid !== id));
    };

    /* ── Loading state ── */
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"
                sx={{ bgcolor: t.rack }}>
                <CircularProgress sx={{ color: t.accent }} />
            </Box>
        );
    }

    /* ── Error state ── */
    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: t.rack, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Paper sx={{
                    p: 5, maxWidth: 480, textAlign: 'center',
                    bgcolor: t.panel, border: `1px solid ${t.border}`, borderRadius: '4px',
                    boxShadow: 'none',
                }}>
                    <ErrorIcon sx={{ fontSize: 56, mb: 2, color: '#ef5350' }} />
                    <Typography variant="h6" sx={{ color: t.ink, fontWeight: 600, letterSpacing: '-0.01em', mb: 1 }}>
                        {error}
                    </Typography>
                    <Typography variant="body2" sx={{ color: t.inkSecond }}>
                        Please contact the sender if you believe this is an error.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    if (!transmittal) return null;

    /* ──────────────────────────────────────────────── */
    /*  Render                                          */
    /* ──────────────────────────────────────────────── */
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: t.rack, py: { xs: 3, sm: 5 } }}>
            <Container maxWidth="md">

                {/* ── Masthead ── */}
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Box
                        component="img"
                        src="/COMPANY_LIGHTMODE_LOGO.png"
                        alt={process.env.COMPANY_NAME || 'Nautilus Dynamics'}
                        sx={{
                            height: 48,
                            mb: 2,
                            filter: t.isDark ? 'brightness(0) invert(1)' : 'none',
                        }}
                    />
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            color: t.ink,
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            textTransform: 'uppercase',
                            fontSize: { xs: '1.1rem', sm: '1.35rem' },
                        }}
                    >
                        Document Transmittal
                    </Typography>
                </Box>

                {/* ── Main Panel ── */}
                <Paper
                    elevation={0}
                    sx={{
                        bgcolor: t.panel,
                        border: `1px solid ${t.border}`,
                        borderRadius: '4px',
                        overflow: 'hidden',
                        mb: 3,
                    }}
                >
                    {/* Status Strip — thin, purposeful */}
                    <Box sx={{
                        px: 3, py: 1.5,
                        bgcolor: acknowledged ? t.successBg : t.infoBg,
                        borderBottom: `1px solid ${t.borderSoft}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                    }}>
                        {acknowledged ? (
                            <>
                                <AckIcon sx={{ fontSize: 18, color: t.successFg }} />
                                <Typography variant="body2" sx={{ color: t.successFg, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                    Receipt Acknowledged
                                </Typography>
                            </>
                        ) : (
                            <>
                                <InfoIcon sx={{ fontSize: 18, color: t.infoFg }} />
                                <Typography variant="body2" sx={{ color: t.infoFg, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                    Ready for Review
                                </Typography>
                            </>
                        )}
                    </Box>

                    <Box sx={{ p: { xs: 3, sm: 4 } }}>

                        {/* ── Document Details (Data Readout) ── */}
                        <Typography
                            variant="overline"
                            sx={{
                                color: t.inkMuted,
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                fontSize: '0.65rem',
                                display: 'block',
                                mb: 1.5,
                            }}
                        >
                            Document Details
                        </Typography>

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 0,
                            mb: 3,
                            border: `1px solid ${t.borderSoft}`,
                            borderRadius: '4px',
                            overflow: 'hidden',
                        }}>
                            {[
                                { label: 'Document Number', value: transmittal.document.nd_number },
                                { label: 'Title', value: transmittal.document.title },
                                { label: 'Status', value: formatRevision(transmittal.document.revision) },
                                { label: 'Project', value: transmittal.document.project },
                            ].map((item, i) => (
                                <Box
                                    key={item.label}
                                    sx={{
                                        px: 2, py: 1.5,
                                        borderBottom: i < 2 ? `1px solid ${t.borderSoft}` : 'none',
                                        borderRight: { xs: 'none', sm: i % 2 === 0 ? `1px solid ${t.borderSoft}` : 'none' },
                                        bgcolor: t.inset,
                                    }}
                                >
                                    <Typography variant="caption" sx={{
                                        color: t.inkMuted,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        display: 'block',
                                        mb: 0.25,
                                    }}>
                                        {item.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: t.ink,
                                        fontWeight: 500,
                                        fontFamily: item.label === 'Document Number' ? '"Roboto Mono", monospace' : 'inherit',
                                    }}>
                                        {item.value || '—'}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* ── Cover Note ── */}
                        {transmittal.message && (
                            <Box sx={{
                                mb: 3, p: 2,
                                bgcolor: t.inset,
                                borderLeft: `3px solid ${t.accent}`,
                                borderRadius: '0 4px 4px 0',
                            }}>
                                <Typography variant="caption" sx={{
                                    display: 'block', mb: 0.5,
                                    color: t.inkMuted,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontSize: '0.65rem',
                                }}>
                                    Message from sender
                                </Typography>
                                <Typography variant="body2" sx={{ color: t.ink, lineHeight: 1.6 }}>
                                    {transmittal.message}
                                </Typography>
                            </Box>
                        )}

                        {/* ── Disclaimer ── */}
                        {!acknowledged && (
                            <Box sx={{
                                mb: 3, px: 2, py: 1.5,
                                bgcolor: t.infoBg,
                                border: `1px solid ${alpha(t.accent, 0.3)}`,
                                borderLeft: `4px solid ${t.accent}`,
                                borderRadius: '0 4px 4px 0',
                                display: 'flex', gap: 1.5, alignItems: 'flex-start',
                            }}>
                                <AckIcon sx={{ fontSize: 18, color: t.infoFg, mt: 0.15, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: t.infoFg, lineHeight: 1.5 }}>
                                    <strong>Acknowledgement notice:</strong> Successfully downloading the official document will automatically mark this transmittal as <strong>Acknowledged</strong> on behalf of your organisation.
                                </Typography>
                            </Box>
                        )}

                        {/* ── Files Table ── */}
                        <Typography
                            variant="overline"
                            sx={{
                                color: t.inkMuted,
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                fontSize: '0.65rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                mb: 1.5,
                            }}
                        >
                            <FileIcon sx={{ fontSize: 16, color: t.inkSecond }} />
                            Files ({transmittal.files.length})
                        </Typography>

                        <TableContainer
                            sx={{
                                mb: 3,
                                border: `1px solid ${t.borderSoft}`,
                                borderRadius: '4px',
                                bgcolor: t.panel,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: t.inset }}>
                                        <TableCell padding="checkbox" sx={{ borderColor: t.borderSoft }}>
                                            <Checkbox
                                                size="small"
                                                checked={selectedFiles.length === transmittal.files.length}
                                                indeterminate={selectedFiles.length > 0 && selectedFiles.length < transmittal.files.length}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                sx={{
                                                    color: t.inkMuted,
                                                    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: t.accent },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{
                                            color: t.inkMuted, fontWeight: 700, fontSize: '0.7rem',
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                            borderColor: t.borderSoft,
                                        }}>Filename</TableCell>
                                        <TableCell sx={{
                                            color: t.inkMuted, fontWeight: 700, fontSize: '0.7rem',
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                            borderColor: t.borderSoft,
                                        }}>Category</TableCell>
                                        <TableCell align="right" sx={{
                                            color: t.inkMuted, fontWeight: 700, fontSize: '0.7rem',
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                            borderColor: t.borderSoft,
                                        }}>Size</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {transmittal.files.map((file) => (
                                        <TableRow
                                            key={file.id}
                                            hover
                                            sx={{
                                                '&:hover': { bgcolor: alpha(t.accent, 0.04) },
                                                '& td': { borderColor: t.borderSoft }
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedFiles.includes(file.id)}
                                                    onChange={(e) => handleSelectFile(file.id, e.target.checked)}
                                                    sx={{
                                                        color: t.inkMuted,
                                                        '&.Mui-checked': { color: t.accent },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 500, color: t.ink, fontSize: '0.85rem' }}>
                                                {file.filename}
                                            </TableCell>
                                            <TableCell>
                                                <FileCategoryChip
                                                    category={file.category}
                                                    labelStyle="short"
                                                    sx={{
                                                        height: 22,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        letterSpacing: '0.03em',
                                                        ...(!['crs'].includes((file.category || '').toLowerCase()) && {
                                                            bgcolor: alpha(t.accent, 0.1),
                                                            color: t.isDark ? t.accent : NAUTILUS.deepOcean,
                                                            border: `1px solid ${alpha(t.accent, 0.25)}`,
                                                        }),
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                color: t.inkSecond,
                                                fontFamily: '"Roboto Mono", monospace',
                                                fontSize: '0.8rem',
                                            }}>
                                                {formatBytes(file.size)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* ── Action Controls ── */}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                        >
                            <Button
                                variant="contained"
                                disableElevation
                                startIcon={downloading ? <CircularProgress size={18} sx={{ color: NAUTILUS.deepOcean }} /> : <DownloadIcon />}
                                disabled={selectedFiles.length === 0 || downloading}
                                onClick={handleDownload}
                                sx={{
                                    bgcolor: t.accent,
                                    color: NAUTILUS.deepOcean,
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    textTransform: 'none',
                                    px: 3, py: 1.25,
                                    borderRadius: '4px',
                                    letterSpacing: '0.01em',
                                    '&:hover': { bgcolor: t.accentHover },
                                    '&.Mui-disabled': {
                                        bgcolor: alpha(t.accent, 0.3),
                                        color: alpha(NAUTILUS.deepOcean, 0.5),
                                    },
                                }}
                            >
                                {downloading ? 'Downloading…' : `Download ${selectedFiles.length > 1 ? 'Selected' : 'File'} (${selectedFiles.length})`}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>

                {/* ── Expiry Footer ── */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    py: 1,
                }}>
                    <ScheduleIcon sx={{ fontSize: 14, color: t.inkMuted }} />
                    <Typography variant="caption" sx={{
                        color: t.inkMuted,
                        fontSize: '0.72rem',
                        letterSpacing: '0.02em',
                    }}>
                        Link expires on {new Date(transmittal.expires_at).toLocaleString()}
                    </Typography>
                </Box>

                {/* ── Brand Footer ── */}
                <Typography variant="caption" display="block" textAlign="center" sx={{
                    color: t.inkMuted,
                    mt: 1,
                    fontSize: '0.65rem',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                }}>
                    {process.env.COMPANY_NAME || 'Nautilus Dynamics'} — Document Management System
                </Typography>
            </Container>
        </Box>
    );
};
